import posthog from 'posthog-js';
import Service, { service } from '@ember/service';
import ENV from 'embroil/config/environment';

export default class Metrics extends Service {
  constructor(...args) {
    super(...args);

    if (ENV.posthogApiKey) {
      posthog.init(ENV.posthogApiKey, {
        api_host: ENV.posthogApiHost || 'https://us.i.posthog.com',
        capture_pageview: true,
        disable_session_recording: false,
        autocapture: true,
      });
      
      this.setupErrorHandlers();
    }
  }

  setupErrorHandlers() {
    window.addEventListener('error', (event) => {
      this.captureException(event.error, {
        type: 'javascript_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        message: event.message,
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.captureException(event.reason, {
        type: 'unhandled_promise_rejection',
      });
    });
  }

  track(eventName, properties = {}) {
    if (ENV.posthogApiKey) {
      posthog.capture(eventName, properties);
    }
  }

  trackViews() {
    // PostHog automatically tracks page views when capture_pageview is true
    // This method is kept for compatibility, but page views are tracked automatically
    if (ENV.posthogApiKey) {
      posthog.capture('$pageview');
    }
  }

  identify(userId, properties = {}) {
    if (ENV.posthogApiKey) {
      posthog.identify(userId, properties);
    }
  }

  reset() {
    if (ENV.posthogApiKey) {
      posthog.reset();
    }
  }

  captureException(error, context = {}) {
    if (!ENV.posthogApiKey) {
      console.error('PostHog not configured, error not captured:', error);
      return;
    }

    // PostHog doesn't have a direct captureException method, so we use capture with $exception event
    const errorData = {
      $exception_type: error.constructor.name,
      $exception_message: error.message,
      $exception_stack: error.stack,
      $exception_source: context.type || 'manual_capture',
      ...context,
    };

    posthog.capture('$exception', errorData);
    
    if (ENV.environment === 'development') {
      console.error('Captured exception:', error, context);
    }
  }

  captureMessage(message, level = 'error', context = {}) {
    if (!ENV.posthogApiKey) {
      console.error('PostHog not configured, message not captured:', message);
      return;
    }

    posthog.capture('$custom_message', {
      message,
      level,
      timestamp: new Date().toISOString(),
      ...context,
    });
  }

  setErrorContext(context = {}) {
    if (ENV.posthogApiKey) {
      // Store context for future error reports
      posthog.register({
        $error_context: context,
        $error_context_timestamp: new Date().toISOString(),
      });
    }
  }

  clearErrorContext() {
    if (ENV.posthogApiKey) {
      posthog.unregister('$error_context');
      posthog.unregister('$error_context_timestamp');
    }
  }

  captureNetworkError(url, status, response, context = {}) {
    if (!ENV.posthogApiKey) return;

    posthog.capture('$network_error', {
      $network_url: url,
      $network_status: status,
      $network_response: response,
      $network_timestamp: new Date().toISOString(),
      ...context,
    });
  }

  captureRouteError(route, error, context = {}) {
    if (!ENV.posthogApiKey) return;

    posthog.capture('$route_error', {
      $route_name: route,
      $route_error: error.message,
      $route_stack: error.stack,
      $route_timestamp: new Date().toISOString(),
      ...context,
    });
  }
}
